import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import customFetch from '../../../api/customFetch.js';

export default function AdminProceduresList() {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const nav = useNavigate();

  useEffect(() => {
    loadProcedures();
  }, []);

  const loadProcedures = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await customFetch.get('/procedures/admin');
      setProcedures(data.procedures || []);
    } catch (e) {
      setError('שגיאה בטעינת הנהלים');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (proc, e) => {
    e.stopPropagation();
    try {
      await customFetch.put(`/procedures/admin/${proc._id}`, { isActive: !proc.isActive });
      setProcedures((prev) =>
        prev.map((r) => (r._id === proc._id ? { ...r, isActive: !r.isActive } : r))
      );
    } catch {
      setError('שגיאה בעדכון סטטוס');
    }
  };

  const handleDelete = async (proc, e) => {
    e.stopPropagation();
    if (!window.confirm(`למחוק את "${proc.title}"?`)) return;
    try {
      await customFetch.delete(`/procedures/admin/${proc._id}`);
      setProcedures((prev) => prev.filter((r) => r._id !== proc._id));
    } catch {
      setError('שגיאה במחיקה');
    }
  };

  const filtered = procedures
    .filter((r) => {
      if (filterActive === 'active') return r.isActive;
      if (filterActive === 'inactive') return !r.isActive;
      return true;
    })
    .filter((r) => {
      if (!searchTerm.trim()) return true;
      return r.title.toLowerCase().includes(searchTerm.toLowerCase());
    });

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>טוען נהלים...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1 className="page-title">
          <span className="page-title-icon">📑</span>
          ניהול נהלים
          <span className="badge info" style={{ marginRight: 8 }}>
            {filtered.length}
          </span>
        </h1>
        <div className="page-actions">
          <button className="admin-btn primary" onClick={() => nav('/admin/procedures/new')}>
            <span>➕</span>
            <span>נוהל חדש</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-alert error">
          <span>⚠️</span>
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="filters-panel">
        <div className="filters-grid">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="admin-label">סינון לפי סטטוס</label>
            <select
              className="admin-select"
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
            >
              <option value="all">הכל ({procedures.length})</option>
              <option value="active">פעילים ({procedures.filter((r) => r.isActive).length})</option>
              <option value="inactive">לא פעילים ({procedures.filter((r) => !r.isActive).length})</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="admin-label">חיפוש</label>
            <input
              type="text"
              className="admin-input"
              placeholder="חפש לפי שם נוהל..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0, alignSelf: 'end' }}>
            <button className="admin-btn secondary" onClick={loadProcedures}>
              🔄 רענן
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      {procedures.length > 0 && (
        <div className="admin-card" style={{ marginTop: 24, marginBottom: 24 }}>
          <div className="card-header">
            <h3 className="card-title">📊 סיכום</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 20, textAlign: 'center' }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--admin-primary)' }}>{procedures.length}</div>
              <div style={{ color: 'var(--admin-text-muted)' }}>סה"כ נהלים</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--admin-success)' }}>
                {procedures.filter((r) => r.isActive).length}
              </div>
              <div style={{ color: 'var(--admin-text-muted)' }}>פעילים</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--admin-text-muted)' }}>
                {procedures.filter((r) => !r.isActive).length}
              </div>
              <div style={{ color: 'var(--admin-text-muted)' }}>לא פעילים</div>
            </div>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📑</div>
          <h3>{procedures.length === 0 ? 'אין נהלים במערכת' : 'לא נמצאו נהלים התואמים לחיפוש'}</h3>
          {procedures.length === 0 && (
            <button className="admin-btn primary" onClick={() => nav('/admin/procedures/new')}>
              ➕ צור נוהל ראשון
            </button>
          )}
        </div>
      ) : (
        <div className="data-grid">
          {filtered.map((proc) => (
            <div
              key={proc._id}
              className="data-item"
              onClick={() => nav(`/admin/procedures/${proc._id}`)}
            >
              <div className="data-item-content">
                <h3>
                  {proc.title}
                  <span className={`badge ${proc.isActive ? 'success' : 'warning'}`}>
                    {proc.isActive ? '🟢 פעיל' : '⚪ לא פעיל'}
                  </span>
                  {proc.fileUrl && <span className="badge info">📎 קובץ</span>}
                  {proc.textContent && <span className="badge info">📄 טקסט</span>}
                </h3>

                {proc.description && (
                  <p style={{ color: 'var(--admin-text-muted)', margin: '4px 0', fontSize: '0.9rem' }}>
                    {proc.description}
                  </p>
                )}

                <div className="data-item-meta">
                  <span><strong>סקטורים:</strong> {proc.targetSectors?.join(', ')}</span>
                  <span>
                    <strong>נוצר:</strong> {new Date(proc.createdAt).toLocaleDateString('he-IL')}
                  </span>
                  {proc.createdBy && (
                    <span><strong>ע"י:</strong> {proc.createdBy.fullName}</span>
                  )}
                </div>
              </div>

              <div className="data-item-actions">
                <button
                  className="admin-btn primary sm"
                  onClick={(e) => { e.stopPropagation(); nav(`/admin/procedures/${proc._id}`); }}
                >
                  ✏️ ערוך
                </button>
                <button
                  className={`admin-btn sm ${proc.isActive ? 'warning' : 'success'}`}
                  onClick={(e) => toggleActive(proc, e)}
                >
                  {proc.isActive ? '🔇 השבת' : '✅ הפעל'}
                </button>
                <button
                  className="admin-btn danger sm"
                  onClick={(e) => handleDelete(proc, e)}
                >
                  🗑️ מחק
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
